import { useCallback, useEffect, useState } from "react";
import { api } from "@shared/api";
import { Banner, Empty, Loading } from "@shared/ui";
import { PLATFORM_LABELS, formatDateTimeIn, type QueueItem } from "@shared/types";

const FILTERS = [
  { key: "scheduled", label: "Upcoming" },
  { key: "published", label: "Published" },
  { key: "failed", label: "Failed" },
  { key: "all", label: "Everything" },
];

export function QueuePage() {
  const [filter, setFilter] = useState("scheduled");
  const [items, setItems] = useState<QueueItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await api.get<{ items: QueueItem[] }>(`/api/admin/queue?status=${filter}`);
      setItems(data.items);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load the queue");
    }
  }, [filter]);

  useEffect(() => {
    setItems(null);
    void load();
  }, [load]);

  async function publishNow(item: QueueItem) {
    setBusy(item.variantId);
    setNote(null);
    setError(null);
    try {
      const res = await api.post<{ ok: boolean; error?: string }>(
        `/api/admin/queue/variants/${item.variantId}/publish`,
      );
      setNote(res.ok ? "Published." : (res.error ?? "It did not go through."));
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not publish");
    } finally {
      setBusy(null);
    }
  }

  async function skip(item: QueueItem) {
    setBusy(item.variantId);
    try {
      await api.post(`/api/admin/queue/variants/${item.variantId}/skip`);
      await load();
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Queue</h1>
          <p className="page-sub">
            Approved posts waiting to go out, and everything that already has.
          </p>
        </div>
      </div>

      {error ? <Banner kind="error">{error}</Banner> : null}
      {note ? <Banner kind="ok">{note}</Banner> : null}

      <div className="actions" style={{ marginBottom: 16 }}>
        {FILTERS.map((f) => (
          <button
            key={f.key}
            className={filter === f.key ? "primary small" : "small"}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {items === null ? (
        <Loading label="Loading queue" />
      ) : items.length === 0 ? (
        <Empty title="Nothing here">
          {filter === "failed"
            ? "No posts have failed. Good."
            : "Approve a plan and its posts will appear here."}
        </Empty>
      ) : (
        items.map((item) => (
          <div className="card" key={item.variantId}>
            <div className="card-head">
              <div style={{ minWidth: 0 }}>
                <h3>
                  {PLATFORM_LABELS[item.platform] ?? item.platform} · {item.channelName}
                </h3>
                <p className="muted" style={{ marginTop: 3 }}>
                  {item.workspaceName} ·{" "}
                  {item.status === "published" && item.publishedAt
                    ? `published ${formatDateTimeIn(item.publishedAt, item.timezone)}`
                    : formatDateTimeIn(item.scheduledAt, item.timezone)}
                </p>
              </div>
              <span
                className={`pill ${
                  item.status === "published" ? "ok" : item.status === "failed" ? "error" : ""
                }`}
              >
                {item.status}
              </span>
            </div>

            <p style={{ marginTop: 10 }}>{item.preview}</p>

            {item.channelStatus !== "connected" ? (
              <p className="muted" style={{ marginTop: 8, color: "var(--warn)" }}>
                This account is {item.channelStatus.replace(/_/g, " ")}. Reconnect it before this
                can publish.
              </p>
            ) : null}

            {item.lastError ? (
              <p className="mono" style={{ marginTop: 10, color: "var(--danger)" }}>
                {item.lastError}
                {item.attempts > 1 ? ` (after ${item.attempts} attempts)` : ""}
              </p>
            ) : null}

            <div className="actions" style={{ marginTop: 14 }}>
              {item.externalUrl ? (
                <a
                  className="pill"
                  href={item.externalUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  View the post ↗
                </a>
              ) : null}
              {item.status !== "published" && item.canPublish ? (
                <button
                  className="small"
                  onClick={() => void publishNow(item)}
                  disabled={busy !== null}
                >
                  {busy === item.variantId
                    ? "Publishing…"
                    : item.status === "failed"
                      ? "Try again"
                      : "Publish now"}
                </button>
              ) : null}
              {item.status !== "published" ? (
                <button className="small" onClick={() => void skip(item)} disabled={busy !== null}>
                  Skip
                </button>
              ) : null}
              {!item.canPublish ? (
                <span className="muted">
                  Publishing to {PLATFORM_LABELS[item.platform] ?? item.platform} is not built yet.
                </span>
              ) : null}
            </div>
          </div>
        ))
      )}
    </>
  );
}
